/*
  vp-ali-reviews.js — Ali Reviews (Kudosi) storefront client.

  Shared tier, no page key: a collection grid or a homepage strip would want the
  same client. Exposes window.VPAliReviews and touches no DOM.

  NO API KEY. The documented API (pub.kudosi.ai/public/*) is Bearer-authenticated
  and would put the account key in the page source of every product page. The
  endpoint below is the one Ali's own widget calls, and it authorises on
  shop_domain alone — which is public in every page already. Nothing to leak,
  no proxy, no scheduled job.

  The cost of that is that this endpoint is UNDOCUMENTED. Everything here was
  established by probing it (Aug 2026); treat the notes as the spec:

    - `product_ids` takes ONE id despite the plural. A comma list returns 400.
    - There is no limit/page/offset/per_page. Page size is chosen by
      `widget_type`: review_box 5, review_popup 10, star_rating/carousel/
      review_tab 15. Every other paging parameter is accepted and ignored,
      silently returning page 1 — which is the failure mode to watch for.
    - `sort_by` only does something for by_content and by_media. by_date and
      by_rating, and `direction` in every combination, are ignored: the order is
      always newest-first. Rating order is therefore assembled here out of the
      per-star streams instead, which is exact rather than approximate.
    - `stars` IS a real server-side filter, and `total_reviews` respects it.
      Five count-only calls therefore produce the whole histogram; checked
      against the authenticated /public/products/rating and identical.
    - `cursor` is base64 of a JSON sort key, and the server issues one only on
      the authenticated endpoint. So it is REBUILT here from the last review of
      the page just received, which is what makes keyless paging work at all.
      Verified over a five-page walk: no gaps, no overlap.

  Because the cursor is ours rather than the server's, callers must dedupe by
  id — createStream does. `content_length` in particular is a length the server
  computed and we are guessing at: code points, not UTF-16 units, which is why
  surrogate pairs are collapsed before measuring. It only participates in
  by_content ordering, so a mismatch cannot affect the date-ordered streams
  used here, and the dedupe covers it if it ever does.
*/
(function () {
  var ENDPOINT = 'https://pub.kudosi.ai/storefront/reviews';

  /* Review avatars come back as a path ('images/avatar/abstract/avatar81.jpg');
     review photos in media_list come back absolute and need no base. */
  var AVATAR_BASE = 'https://cdn.alireviews.io/';

  var WIDGET_LIST = 'review_tab'; // 15 per page — the largest on offer
  var WIDGET_COUNT = 'review_box'; // 5 per page — for calls we read only the total off
  var PAGE_SIZE = 15;

  var CACHE_PREFIX = 'vp-ali:v1:';
  var CACHE_TTL = 10 * 60 * 1000;

  function cacheGet(key) {
    try {
      var raw = window.sessionStorage.getItem(CACHE_PREFIX + key);
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (!entry || Date.now() - entry.t > CACHE_TTL) return null;
      return entry.v;
    } catch (e) {
      return null;
    }
  }

  function cacheSet(key, value) {
    try {
      window.sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ t: Date.now(), v: value }));
    } catch (e) {
      /* private mode, quota, blocked site data — the cache is an optimisation */
    }
  }

  function requestPage(opts) {
    var widget = opts.widget || WIDGET_LIST;
    var params = [
      'shop_domain=' + encodeURIComponent(opts.shopDomain),
      'product_ids=' + encodeURIComponent(opts.productId),
      'widget_type=' + encodeURIComponent(widget),
      'sort_by=by_date'
    ];
    if (opts.stars) params.push('stars=' + encodeURIComponent(opts.stars));
    if (opts.cursor) params.push('cursor=' + encodeURIComponent(opts.cursor));

    var url = ENDPOINT + '?' + params.join('&');
    var key = opts.productId + '|' + widget + '|' + (opts.stars || '') + '|' + (opts.cursor || '');

    var cached = cacheGet(key);
    if (cached) return Promise.resolve(cached);

    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Ali Reviews responded ' + response.status);
        return response.json();
      })
      .then(function (data) {
        var page = {
          reviews: (data && data.reviews) || [],
          total: data && typeof data.total_reviews === 'number' ? data.total_reviews : 0,
          hasMore: !!(data && data.has_more)
        };
        cacheSet(key, page);
        return page;
      });
  }

  /* Code points, not UTF-16 units — see the header note. */
  function codePointLength(value) {
    return String(value || '').replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, '_').length;
  }

  function buildCursor(review) {
    var media = review.media_list || [];
    var videos = 0;
    var images = 0;

    for (var i = 0; i < media.length; i++) {
      if (media[i] && media[i].type === 'video') videos++;
      else images++;
    }

    /* Key order mirrors a cursor decoded from the authenticated endpoint. The
       payload is ASCII throughout, so btoa needs no UTF-8 dance. */
    return window.btoa(
      JSON.stringify({
        id: review.id,
        star: review.star,
        content_length: codePointLength(review.content),
        total_media: media.length,
        total_video: videos,
        total_image: images,
        pinned_at: review.pinned_at,
        created_at: review.created_at
      })
    );
  }

  /*
    A stream is one ordered sequence of reviews, loaded on demand.

    order 'newest' is a single source in the API's own order. 'highest' and
    'lowest' are the per-star streams walked in turn — 5,4,3,2,1 or the reverse
    — which is how rating order gets served exactly despite the endpoint not
    sorting by rating. A stream already filtered to one star has nothing left to
    order, so it stays a single source whatever the order asks for.
  */
  function createStream(opts) {
    var queue;

    if (opts.stars) {
      queue = [opts.stars];
    } else if (opts.order === 'highest') {
      queue = [5, 4, 3, 2, 1];
    } else if (opts.order === 'lowest') {
      queue = [1, 2, 3, 4, 5];
    } else {
      queue = [null];
    }

    var stream = {
      items: [],
      total: 0,
      exhausted: false,
      loading: false
    };

    var index = 0;
    var cursor = null;
    var seen = {};

    function loadNext() {
      if (index >= queue.length) {
        stream.exhausted = true;
        return Promise.resolve(stream);
      }

      return requestPage({
        shopDomain: opts.shopDomain,
        productId: opts.productId,
        stars: queue[index],
        cursor: cursor
      }).then(function (page) {
        for (var i = 0; i < page.reviews.length; i++) {
          var review = page.reviews[i];
          if (!review || !review.id || seen[review.id]) continue;
          seen[review.id] = true;
          stream.items.push(review);
        }

        /* Only a single-source stream can take its total from the response. A
           bucket walk has to sum them, and does so as each bucket is reached. */
        if (queue.length === 1) {
          stream.total = page.total;
        } else if (!cursor) {
          stream.total += page.total;
        }

        /* An empty page ends a bucket even when has_more says otherwise: a
           cursor we built ourselves is the likelier thing to be wrong, and
           trusting the flag over the payload would spin. */
        if (page.hasMore && page.reviews.length) {
          cursor = buildCursor(page.reviews[page.reviews.length - 1]);
        } else {
          index++;
          cursor = null;
        }

        if (index >= queue.length) stream.exhausted = true;
        return stream;
      });
    }

    /* Resolves once the stream holds `count` items or has run out. Sequential
       by necessity — each page's cursor comes out of the one before it. */
    stream.ensure = function (count) {
      if (stream.items.length >= count || stream.exhausted) return Promise.resolve(stream);

      stream.loading = true;
      return loadNext()
        .then(function () {
          return stream.ensure(count);
        })
        .then(function () {
          stream.loading = false;
          return stream;
        })
        .catch(function (error) {
          stream.loading = false;
          throw error;
        });
    };

    return stream;
  }

  /*
    Average, total and the five histogram rows, from five count-only calls run
    in parallel. The average is computed from the buckets rather than asked for,
    which keeps it consistent with the bars underneath it by construction — the
    exact failure the section was built to avoid on the blocks path.
  */
  function fetchHistogram(opts) {
    var stars = [1, 2, 3, 4, 5];

    return Promise.all(
      stars.map(function (star) {
        return requestPage({
          shopDomain: opts.shopDomain,
          productId: opts.productId,
          stars: star,
          widget: WIDGET_COUNT
        }).then(function (page) {
          return page.total;
        });
      })
    ).then(function (totals) {
      var counts = {};
      var total = 0;
      var weighted = 0;

      for (var i = 0; i < stars.length; i++) {
        counts[stars[i]] = totals[i];
        total += totals[i];
        weighted += totals[i] * stars[i];
      }

      return {
        counts: counts,
        total: total,
        average: total ? Math.round((weighted / total) * 10) / 10 : 0
      };
    });
  }

  function avatarUrl(raw) {
    if (!raw) return '';
    if (raw.indexOf('http://') === 0 || raw.indexOf('https://') === 0 || raw.indexOf('//') === 0) return raw;
    return AVATAR_BASE + raw.replace(/^\/+/, '');
  }

  window.VPAliReviews = {
    PAGE_SIZE: PAGE_SIZE,
    createStream: createStream,
    fetchHistogram: fetchHistogram,
    avatarUrl: avatarUrl
  };
})();
